import { formatDateRu, formatShiftWindow } from '@pms/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuthStore } from '../auth/store.js';
import { currentShiftFor, newDraft, saveDraft } from '../reports/draft.js';
import { db } from '../sync/db.js';

/**
 * Начало сдачи смены: участок → агрегат → напарник.
 *
 * Дату и номер смены система определяет сама по времени на устройстве:
 * в семь утра после ночной смены легко ошибиться на сутки, а ошибка в дате
 * ломает всю аналитику.
 */
export function ReportStartScreen(): JSX.Element {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);

  const [siteId, setSiteId] = useState<string | null>(null);
  const [machineId, setMachineId] = useState<string | null>(null);
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [shift, setShift] = useState<{ date: string; shiftNumber: number } | null>(null);

  const sites = useLiveQuery(() => db.sites.orderBy('sortOrder').toArray(), [], []);
  const machines = useLiveQuery(
    async () =>
      siteId ? (await db.machines.where('siteId').equals(siteId).sortBy('sortOrder')) : [],
    [siteId],
    [],
  );
  const templates = useLiveQuery(
    async () => (siteId ? await db.shiftTemplates.where('siteId').equals(siteId).toArray() : []),
    [siteId],
    [],
  );
  const mates = useLiveQuery(
    async () => (await db.users.where('role').equals('OPERATOR').sortBy('fullName')),
    [],
    [],
  );

  useEffect(() => {
    if (!siteId) {
      setShift(null);
      return;
    }
    void currentShiftFor(siteId).then(setShift);
  }, [siteId]);

  const machine = machines.find((item) => item.id === machineId);
  const site = sites.find((item) => item.id === siteId);
  const window = templates.find(
    (template) => template.shiftNumber === shift?.shiftNumber && template.validTo === null,
  );

  const canPickPartner = (machine?.maxOperators ?? 1) > 1;

  const start = async (): Promise<void> => {
    if (!user || !siteId || !machineId || !shift) return;
    const userIds = partnerId ? [user.id, partnerId] : [user.id];
    const draft = newDraft({ siteId, machineId, date: shift.date, shiftNumber: shift.shiftNumber, userIds });
    await saveDraft(draft);
    navigate(`/report/${draft.id}`);
  };

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Сдать смену</h1>
        <Link to="/" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      <section className="mb-4">
        <h2 className="mb-3 text-lg font-semibold">Участок</h2>
        <div className="flex flex-col gap-touch">
          {sites.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setSiteId(item.id);
                setMachineId(null);
                setPartnerId(null);
              }}
              className={`min-h-touch rounded-xl px-5 py-4 text-left text-lg font-semibold ${
                item.id === siteId ? 'bg-sky-700 text-white' : 'bg-card shadow-card'
              }`}
            >
              {item.name}
            </button>
          ))}
        </div>
      </section>

      {siteId && (
        <section className="mb-4">
          <h2 className="mb-3 text-lg font-semibold">Агрегат</h2>
          <div className="flex flex-col gap-touch">
            {machines
              .filter((item) => item.isActive)
              .map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    setMachineId(item.id);
                    setPartnerId(null);
                  }}
                  className={`min-h-touch rounded-xl px-5 py-4 text-left text-lg font-semibold ${
                    item.id === machineId ? 'bg-sky-700 text-white' : 'bg-card shadow-card'
                  }`}
                >
                  {item.name}
                </button>
              ))}
          </div>
        </section>
      )}

      {machineId && canPickPartner && (
        <section className="mb-4">
          <h2 className="mb-3 text-lg font-semibold">Напарник</h2>
          <p className="mb-3 text-base text-dim">
            Если работали вдвоём, выработка разделится поровну
          </p>
          <div className="flex flex-col gap-touch">
            <button
              type="button"
              onClick={() => setPartnerId(null)}
              className={`min-h-touch rounded-xl px-5 py-4 text-left text-lg font-semibold ${
                partnerId === null ? 'bg-sky-700 text-white' : 'bg-card shadow-card'
              }`}
            >
              Работал один
            </button>
            {mates
              .filter((mate) => mate.isActive && mate.id !== user?.id)
              .map((mate) => (
                <button
                  key={mate.id}
                  type="button"
                  onClick={() => setPartnerId(mate.id)}
                  className={`min-h-touch rounded-xl px-5 py-4 text-left text-lg font-semibold ${
                    mate.id === partnerId ? 'bg-sky-700 text-white' : 'bg-card shadow-card'
                  }`}
                >
                  {mate.fullName}
                </button>
              ))}
          </div>
        </section>
      )}

      {machineId && (
        <section className="mb-4 rounded-xl bg-card shadow-card p-4">
          {shift ? (
            <>
              <p className="text-lg font-semibold">
                Смена {shift.shiftNumber} за {formatDateRu(shift.date)}
              </p>
              {window && (
                <p className="text-base text-dim">
                  {formatShiftWindow({
                    shiftNumber: window.shiftNumber,
                    startMinute: window.startMinute,
                    durationMinutes: window.durationMinutes,
                  })}
                </p>
              )}
              <p className="mt-2 text-base text-dim">
                Смена относится к дате своего начала — ночная считается за прошедшие сутки
              </p>
            </>
          ) : (
            <p className="text-base text-amber-700">
              Сейчас {site?.name ?? 'участок'} не работает по графику смен. Отчёт сдаёт
              администратор.
            </p>
          )}
        </section>
      )}

      <button
        type="button"
        disabled={!siteId || !machineId || !shift}
        onClick={() => void start()}
        className="w-full rounded-xl bg-sky-600 px-5 py-5 text-lg font-semibold text-white active:bg-sky-700 disabled:opacity-40"
      >
        Начать
      </button>
    </div>
  );
}
